import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, MapPin, Calendar, X, Search } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Pagination } from "@/components/Pagination";

const EVENT_TYPES = ["wedding", "mehendi", "sangeet", "haldi", "reception", "engagement", "trial", "meeting"];
const SLOT_OPTIONS = ["Morning", "Afternoon", "Evening", "Others"];

function formatDate(d) {
  try { return format(parseISO(d), "EEE, dd MMM yyyy"); } catch { return d; }
}

function eventTypeLabel(t) {
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
}

function EventFormModal({ open, onClose, onSaved, users, weddings }) {
  const initial = {
    title: "", wedding_id: "", couple_names: "", event_type: "wedding", event_date: "",
    ready_by: "", slots: [], location: "", notes: "", total_price: 0, currency: "INR",
    assigned: [],
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [assignSelect, setAssignSelect] = useState("");
  const [weddingSearch, setWeddingSearch] = useState("");

  useEffect(() => { if (open) { setForm(initial); setWeddingSearch(""); } /* eslint-disable-next-line */ }, [open]);

  if (!open) return null;

  const confirmedWeddings = weddings.filter((w) => (w.status || "pending") === "confirmed");
  const term = weddingSearch.trim().toLowerCase();
  const searchMatches = term
    ? confirmedWeddings.filter((w) =>
        (w.couple_names || "").toLowerCase().includes(term) ||
        (w.contact_phone || "").toLowerCase().includes(term) ||
        (w.instagram_id || "").toLowerCase().includes(term) ||
        (w.location || "").toLowerCase().includes(term)
      ).slice(0, 6)
    : [];
  const selectedWedding = confirmedWeddings.find((w) => w.id === form.wedding_id);

  const addAssignment = () => {
    if (!assignSelect) return;
    const user = users.find((u) => u.id === assignSelect);
    if (!user) return;
    if (form.assigned.some((a) => a.team_member_id === user.id)) return;
    setForm({ ...form, assigned: [...form.assigned, { team_member_id: user.id, service_type: user.service_types?.[0] || "makeup", agreed_amount: 0 }] });
    setAssignSelect("");
  };

  const removeAssignment = (id) => setForm({ ...form, assigned: form.assigned.filter((a) => a.team_member_id !== id) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.wedding_id) { toast.error("Please select a confirmed wedding to link this event to"); return; }
    setSaving(true);
    try {
      const payload = { ...form, total_price: Number(form.total_price) || 0, assigned: form.assigned.map((a) => ({ ...a, agreed_amount: Number(a.agreed_amount) || 0 })) };
      await api.post("/events", payload);
      toast.success("Event created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="event-form-modal">
      <div className="bg-white border border-[#EAE7E0] max-w-2xl w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#EAE7E0]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652]">New</div>
            <h2 className="font-serif text-3xl tracking-tight">Create Event</h2>
          </div>
          <button onClick={onClose} className="text-[#5C5652] hover:text-[#2A2421]" data-testid="close-event-form"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-6">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Wedding * <span className="text-[#5C5652]/60 normal-case tracking-normal">(only confirmed weddings)</span></label>
            {selectedWedding ? (
              <div className="flex items-center justify-between border border-[#EAE7E0] p-3 bg-[#FAF9F6]" data-testid="selected-wedding">
                <div>
                  <div className="text-sm text-[#2A2421]">{selectedWedding.couple_names}</div>
                  <div className="text-xs text-[#5C5652]">{selectedWedding.location || "—"}</div>
                </div>
                <button type="button" onClick={() => setForm({ ...form, wedding_id: "", couple_names: "" })} data-testid="clear-wedding"
                  className="text-xs uppercase tracking-[0.2em] text-[#5C5652] hover:text-red-700">Change</button>
              </div>
            ) : (
              <div className="relative">
                <input value={weddingSearch} onChange={(e) => setWeddingSearch(e.target.value)} data-testid="wedding-search-input"
                  placeholder="Search by couple, phone, Instagram or location…"
                  className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                {searchMatches.length > 0 && (
                  <div className="absolute z-10 top-full left-0 right-0 bg-white border border-[#EAE7E0] mt-1 shadow-sm max-h-60 overflow-y-auto" data-testid="wedding-search-results">
                    {searchMatches.map((w) => (
                      <button key={w.id} type="button"
                        onClick={() => { setForm({ ...form, wedding_id: w.id, couple_names: w.couple_names }); setWeddingSearch(""); }}
                        data-testid={`wedding-search-option-${w.id}`}
                        className="w-full text-left px-4 py-3 hover:bg-[#FAF9F6] border-b border-[#EAE7E0] last:border-0">
                        <div className="text-sm text-[#2A2421]">{w.couple_names}</div>
                        <div className="text-xs text-[#5C5652]">{w.location || "—"}</div>
                      </button>
                    ))}
                  </div>
                )}
                {confirmedWeddings.length === 0 && (
                  <div className="text-xs text-[#C88270] mt-2">No confirmed weddings yet. Create one from Weddings → mark it confirmed.</div>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Title" required>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" placeholder="Mehendi ceremony" />
            </Field>
            <Field label="Event Type">
              <input list="event-type-options" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="event-type-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" placeholder="mehendi, cocktail, pool-party…" />
              <datalist id="event-type-options">
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{eventTypeLabel(t)}</option>)}
              </datalist>
            </Field>
            <Field label="Date" required>
              <input required type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="event-date-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </Field>
            <Field label="Ready by">
              <input type="time" value={form.ready_by} onChange={(e) => setForm({ ...form, ready_by: e.target.value })} data-testid="event-readyby-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </Field>
            <Field label="Location">
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-location-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" placeholder="Taj Palace, Delhi" />
            </Field>
            <Field label={`Total Price (${form.currency})`}>
              <input type="number" min="0" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} data-testid="event-price-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </Field>
          </div>

          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="event-notes-input"
              rows={3} className="w-full border border-[#EAE7E0] bg-transparent p-3 focus:border-[#C88270] focus:outline-none" />
          </Field>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-3">Slot to be blocked</div>
            <div className="flex flex-wrap gap-2" data-testid="event-slots">
              {SLOT_OPTIONS.map((s) => (
                <button type="button" key={s} data-testid={`slot-${s.toLowerCase()}`}
                  onClick={() => setForm({ ...form, slots: form.slots.includes(s) ? [] : [s] })}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${form.slots.includes(s) ? "bg-[#C88270] text-white border-[#C88270]" : "border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421]"}`}>{s}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-3">Team assigned</div>
            <div className="flex gap-2 mb-3">
              <select value={assignSelect} onChange={(e) => setAssignSelect(e.target.value)} data-testid="assign-team-select"
                className="flex-1 border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none">
                <option value="">Select team member…</option>
                {users.filter((u) => u.role !== "admin").map((u) => (
                  <option key={u.id} value={u.id}>{u.name} — {u.service_types?.join(", ") || "team"}</option>
                ))}
              </select>
              <button type="button" onClick={addAssignment} data-testid="assign-add-button"
                className="px-4 py-2 border border-[#2A2421] text-[#2A2421] text-xs uppercase tracking-[0.2em] hover:bg-[#2A2421] hover:text-white transition-colors">Add</button>
            </div>
            {form.assigned.map((a) => {
              const u = users.find((x) => x.id === a.team_member_id);
              return (
                <div key={a.team_member_id} className="flex items-center gap-3 py-2 border-b border-[#EAE7E0]" data-testid={`assigned-row-${a.team_member_id}`}>
                  <div className="flex-1 text-sm">{u?.name}</div>
                  <input value={a.service_type} onChange={(e) => setForm({ ...form, assigned: form.assigned.map((x) => x.team_member_id === a.team_member_id ? { ...x, service_type: e.target.value } : x) })}
                    className="w-40 border-b border-[#EAE7E0] py-1 text-sm focus:border-[#C88270] focus:outline-none" placeholder="Service" />
                  <button type="button" onClick={() => removeAssignment(a.team_member_id)} className="text-[#5C5652] hover:text-red-600"><X size={16} /></button>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[#EAE7E0]">
            <button type="button" onClick={onClose} className="px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#5C5652] hover:text-[#2A2421]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="event-submit-button"
              className="px-6 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Create event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">
        {label}{required && <span className="text-[#C88270]"> *</span>}
      </label>
      {children}
    </div>
  );
}

export default function EventsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1" && isAdmin) {
      setModalOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin]);

  // Weddings + users only needed by admin (for the create-event modal)
  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([api.get("/users"), api.get("/weddings")])
      .then(([u, w]) => { setUsers(u.data); setWeddings(w.data); })
      .catch(() => { /* ignore */ });
  }, [isAdmin]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [filter, debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE, scope: filter, search: debouncedSearch };
      if (!isAdmin) params.assigned_status = "available";
      const { data } = await api.get("/events", { params });
      setEvents(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, filter, debouncedSearch, isAdmin]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="events-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Schedule</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421]">Events</h1>
        </div>
        {isAdmin && (
          <button onClick={() => setModalOpen(true)} data-testid="new-event-button"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors self-start">
            <Plus size={16} /> New Event
          </button>
        )}
      </div>

      <div className="relative mb-6">
        <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#5C5652]" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="events-search-input"
          placeholder="Search by couple, title, location, type or slot…"
          className="w-full pl-7 pr-3 py-2 border-b border-[#EAE7E0] bg-transparent focus:border-[#C88270] focus:outline-none text-sm" />
      </div>

      <div className="flex gap-6 mb-8 border-b border-[#EAE7E0]">
        {["upcoming", "past", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`pb-3 text-xs uppercase tracking-[0.25em] transition-colors ${filter === f ? "text-[#C88270] border-b-2 border-[#C88270]" : "text-[#5C5652] hover:text-[#2A2421]"}`}>
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : total === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]">
          No events to show.
        </div>
      ) : (
        <div className="hidden md:block bg-white border border-[#EAE7E0] overflow-x-auto" data-testid="events-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#EAE7E0]">
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Date</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Ready by</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Type</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Title</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Location</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652] text-right">Team</th>
                <th className="py-4 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id} className="border-b border-[#EAE7E0] last:border-0 hover:bg-[#FAF9F6]/60" data-testid={`event-row-${ev.id}`}>
                  <td className="py-4 px-6 text-sm text-[#2A2421] whitespace-nowrap">{formatDate(ev.event_date)}</td>
                  <td className="py-4 px-6 text-sm text-[#5C5652]">
                    <div>{ev.ready_by || "—"}</div>
                    {ev.slots?.length > 0 && <div className="text-xs text-[#C88270]">{ev.slots.join(", ")}</div>}
                  </td>
                  <td className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#C88270]">{eventTypeLabel(ev.event_type)}</td>
                  <td className="py-4 px-6 text-sm text-[#2A2421]">{ev.couple_names || ev.title}<div className="text-xs text-[#5C5652]">{ev.title}</div></td>
                  <td className="py-4 px-6 text-sm text-[#5C5652]">{ev.location || "—"}</td>
                  <td className="py-4 px-6 text-sm text-right">
                    {isAdmin ? (() => {
                      const avail = (ev.assigned || []).filter((a) => a.status === "available").length;
                      return <span data-testid={`event-team-count-${ev.id}`} className={avail < 2 ? "text-red-600 font-medium" : "text-[#829384] font-medium"}>{avail}/{ev.assigned?.length || 0}{avail < 2 && " ⚠"}</span>;
                    })() : <span className="text-[#2A2421]">{ev.assigned?.length || 0}</span>}
                  </td>
                  <td className="py-4 px-6 text-right"><Link to={`/events/${ev.id}`} className="text-[10px] uppercase tracking-[0.25em] text-[#C88270] hover:text-[#B37363]" data-testid={`open-event-${ev.id}`}>Open →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && (
        <div className="md:hidden space-y-4" data-testid="events-mobile-list">
          {events.map((ev) => {
            const avail = (ev.assigned || []).filter((a) => a.status === "available").length;
            return (
              <Link key={ev.id} to={`/events/${ev.id}`} data-testid={`event-mcard-${ev.id}`}
                className="block bg-white border border-[#EAE7E0] rounded-2xl p-4 shadow-[0_2px_12px_-4px_rgba(42,36,33,0.05)] transition-transform active:scale-[0.99]">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#C88270]">{eventTypeLabel(ev.event_type)}</div>
                    <div className="font-serif text-lg leading-tight text-[#2A2421] truncate">{ev.couple_names || ev.title}</div>
                    <div className="text-[12px] text-[#5C5652] flex flex-wrap items-center gap-x-2 mt-0.5">
                      <span className="whitespace-nowrap">{formatDate(ev.event_date)}</span>
                      {ev.ready_by && <span className="text-[#C88270]">· {ev.ready_by}</span>}
                      {ev.slots?.length > 0 && <span className="text-[#C88270]">· {ev.slots.join(", ")}</span>}
                    </div>
                    {ev.location && <div className="text-[12px] text-[#5C5652] truncate flex items-center gap-1 mt-0.5"><MapPin size={12} strokeWidth={1.5} /> {ev.location}</div>}
                  </div>
                  {isAdmin ? (
                    <span data-testid={`event-mteam-${ev.id}`} className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ${avail < 2 ? "bg-red-50 text-red-600" : "bg-[#829384]/15 text-[#829384]"}`}>{avail}/{ev.assigned?.length || 0}</span>
                  ) : (
                    <span className="shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold bg-[#EAE7E0]/60 text-[#5C5652]">{ev.assigned?.length || 0}</span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {!loading && total > 0 && (
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} testId="events-pagination" />
      )}

      <EventFormModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} users={users} weddings={weddings} />
    </div>
  );
}
